// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { CASE_NOTE_HASH_LABEL_REWRITE_FAMILY, CASE_NOTE_HASH_LABEL_REWRITE_SECTION_REGEX_SOURCE } from './CaseBuddyAppConstants.js';
import { duplicateHashNaturalCompare } from './CaseBuddyDuplicateHashLabeler.js';
import { caseBuddyHashString, firstNonEmptyEntryValue, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export function noteRewriteIsWordChar(ch) {
  return !!ch && /[A-Za-z0-9_]/.test(ch);
}

export function noteRewriteBoundaryOk(text = '', start = 0, end = 0, pattern = '') {
  const pat = String(pattern || '');
  if (!pat) return false;
  const first = pat[0];
  const last = pat[pat.length - 1];
  const before = start > 0 ? text[start - 1] : '';
  const after = end + 1 < text.length ? text[end + 1] : '';
  if (noteRewriteIsWordChar(first) && noteRewriteIsWordChar(before)) return false;
  if (noteRewriteIsWordChar(last) && noteRewriteIsWordChar(after)) return false;
  return true;
}

export function noteRewriteIsLikelyPathLabel(value = '') {
  const label = normalizeCell(value);
  if (!label || /\s/.test(label)) return false;
  if (/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/.test(label) && /\d|[A-Z]/.test(label)) return true;
  return false;
}

export function noteRewriteAlphaToNumber(alpha = '') {
  const s = String(alpha || '').toLowerCase();
  let n = 0;
  for (let index = 0; index < s.length; index += 1) {
    const code = s.charCodeAt(index);
    if (code < 97 || code > 122) return Number.NaN;
    n = n * 26 + (code - 96);
  }
  return n;
}

export function noteRewriteTokenizePathCore(core = '') {
  const trimmed = normalizeCell(core);
  if (!trimmed || !/^[0-9A-Za-z.]+$/.test(trimmed)) return null;
  const parts = trimmed.match(/(\d+|[a-zA-Z]+|\.)/g);
  if (!parts || parts.join('') !== trimmed) return null;
  return parts.map((part) => {
    if (part === '.') return { type: 'dot', value: 0, raw: part };
    if (/^\d+$/.test(part)) return { type: 'num', value: Number.parseInt(part, 10), raw: part };
    return { type: 'alpha', value: noteRewriteAlphaToNumber(part), raw: part.toLowerCase() };
  });
}

export function noteRewriteRootKeyFromTokens(tokens = []) {
  const first = Array.isArray(tokens) ? tokens[0] : null;
  if (!first) return '';
  if (first.type === 'num') return `N:${first.value}`;
  if (first.type === 'alpha') return `A:${first.value}`;
  return '';
}

export function noteRewriteParsePathLabel(label = '') {
  const original = normalizeCell(label);
  if (!original) return null;
  const direct = noteRewriteTokenizePathCore(original);
  if (direct) {
    return { original, prefixNorm: '', suffix: original, tokens: direct, rootKey: noteRewriteRootKeyFromTokens(direct) };
  }
  let idx = original.lastIndexOf('-');
  while (idx > 0) {
    const left = original.slice(0, idx).trim();
    const right = original.slice(idx + 1).trim();
    const tokens = noteRewriteTokenizePathCore(right);
    if (left && right && tokens) {
      return { original, prefixNorm: `${left}-`.toLowerCase(), suffix: right, tokens, rootKey: noteRewriteRootKeyFromTokens(tokens) };
    }
    idx = original.lastIndexOf('-', idx - 1);
  }
  return null;
}

export function noteRewriteCompareTokenLists(aTokens = [], bTokens = []) {
  const rank = { num: 0, dot: 1, alpha: 2 };
  const length = Math.max(aTokens.length, bTokens.length);
  for (let index = 0; index < length; index += 1) {
    const a = aTokens[index];
    const b = bTokens[index];
    if (!a) return -1;
    if (!b) return 1;
    if (a.type !== b.type) return (rank[a.type] || 9) - (rank[b.type] || 9);
    if ((a.type === 'num' || a.type === 'alpha') && a.value !== b.value) return a.value - b.value;
    if (a.raw && b.raw && a.raw.length !== b.raw.length) return a.raw.length - b.raw.length;
  }
  return 0;
}

export function noteRewriteCompareParsedLabels(left = null, right = null) {
  const leftPrefix = left?.prefixNorm || '';
  const rightPrefix = right?.prefixNorm || '';
  if (leftPrefix !== rightPrefix) return leftPrefix.localeCompare(rightPrefix, undefined, { numeric: true, sensitivity: 'base' });
  return noteRewriteCompareTokenLists(left?.tokens || [], right?.tokens || []);
}

export function noteRewriteCompareLabels(left = '', right = '') {
  const leftParsed = noteRewriteParsePathLabel(left);
  const rightParsed = noteRewriteParsePathLabel(right);
  if (leftParsed && rightParsed) return noteRewriteCompareParsedLabels(leftParsed, rightParsed);
  if (leftParsed && !rightParsed) return -1;
  if (!leftParsed && rightParsed) return 1;
  return duplicateHashNaturalCompare(left, right);
}

export function noteRewriteCommonPrefixTokenCount(aTokens = [], bTokens = []) {
  const length = Math.min(aTokens?.length || 0, bTokens?.length || 0);
  let index = 0;
  for (; index < length; index += 1) {
    const a = aTokens[index];
    const b = bTokens[index];
    if (!a || !b || a.type !== b.type) break;
    if ((a.type === 'num' || a.type === 'alpha') && a.value !== b.value) break;
  }
  return index;
}

export function noteRewriteNormalizeHash(value = '') {
  return normalizeCell(value);
}

export function noteRewriteNormalizeHashInsensitive(value = '') {
  return normalizeCell(value).toLowerCase();
}

export function buildNoteRewriteHashCandidateMaps(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  const exact = new Map();
  const insensitive = new Map();
  const entries = parseCaseEntries(data);

  entries.forEach((entry) => {
    const lead = entry?.rows?.[0] || { rowIndex: entry?.startRow || 0, row: [] };
    const leadRow = Array.isArray(lead.row) ? lead.row : [];
    const hash = noteRewriteNormalizeHash(entry?.hash || leadRow[1]);
    const label = normalizeCell(firstNonEmptyEntryValue(entry?.rows || [], 0) || leadRow[0]);
    if (!hash || !label) return;
    const parsed = noteRewriteParsePathLabel(label);
    const candidate = {
      hash,
      hashInsensitive: noteRewriteNormalizeHashInsensitive(hash),
      label,
      rowIndex: Number.isInteger(lead.rowIndex) ? lead.rowIndex : entry.startRow,
      rowNumber: (Number.isInteger(lead.rowIndex) ? lead.rowIndex : entry.startRow) + 1,
      parsed,
      prefixNorm: parsed?.prefixNorm || '',
      rootKey: parsed?.rootKey || '',
      tokens: parsed?.tokens || []
    };
    if (!exact.has(hash)) exact.set(hash, []);
    if (!exact.get(hash).some(item => item.label === label)) exact.get(hash).push(candidate);
    const insensitiveKey = candidate.hashInsensitive;
    if (!insensitive.has(insensitiveKey)) insensitive.set(insensitiveKey, []);
    if (!insensitive.get(insensitiveKey).some(item => item.hash === hash && item.label === label)) insensitive.get(insensitiveKey).push(candidate);
  });

  return { exact, insensitive };
}

export function noteRewriteSelectBestCandidate(candidates = [], contextLabel = '', currentRowIndex = 0) {
  const source = Array.isArray(candidates) ? candidates.filter(item => item?.label) : [];
  if (source.length === 0) return null;
  if (source.length === 1) return source[0];
  const contextParsed = noteRewriteParsePathLabel(contextLabel);
  const contextPrefix = contextParsed?.prefixNorm || '';
  const contextRootKey = contextParsed?.rootKey || '';
  const contextTokens = contextParsed?.tokens || [];
  const sorted = source.slice().sort((left, right) => {
    const leftPrefix = !!contextPrefix && left.prefixNorm === contextPrefix;
    const rightPrefix = !!contextPrefix && right.prefixNorm === contextPrefix;
    if (leftPrefix !== rightPrefix) return leftPrefix ? -1 : 1;
    const leftRoot = !!contextRootKey && left.rootKey === contextRootKey;
    const rightRoot = !!contextRootKey && right.rootKey === contextRootKey;
    if (leftRoot !== rightRoot) return leftRoot ? -1 : 1;
    const leftShared = noteRewriteCommonPrefixTokenCount(contextTokens, left.tokens || []);
    const rightShared = noteRewriteCommonPrefixTokenCount(contextTokens, right.tokens || []);
    if (leftShared !== rightShared) return rightShared - leftShared;
    const leftDistance = Math.abs((left.rowIndex || 0) - currentRowIndex);
    const rightDistance = Math.abs((right.rowIndex || 0) - currentRowIndex);
    if (leftDistance !== rightDistance) return leftDistance - rightDistance;
    return noteRewriteCompareLabels(left.label, right.label);
  });
  return sorted[0];
}

export function noteRewriteFindCandidatesForHash(hash = '', maps = {}) {
  const exactHash = noteRewriteNormalizeHash(hash);
  if (!exactHash) return [];
  const exact = maps?.exact?.get(exactHash);
  if (exact?.length) return exact;
  return maps?.insensitive?.get(noteRewriteNormalizeHashInsensitive(exactHash)) || [];
}

export function noteRewriteResolveHashToLabel(hash = '', maps = {}, contextLabel = '', rowIndex = 0) {
  const candidates = noteRewriteFindCandidatesForHash(hash, maps);
  const selected = noteRewriteSelectBestCandidate(candidates, contextLabel, rowIndex);
  return {
    hash: noteRewriteNormalizeHash(hash),
    label: selected?.label || '',
    candidates,
    ambiguous: candidates.length > 1,
    selectedRowNumber: selected?.rowNumber || ''
  };
}

export function noteRewriteSplitStructuredList(value = '') {
  const text = normalizeCell(value);
  if (!text) return [];
  return text
    .replace(/,\s*(and|&)\s+/gi, ', ')
    .replace(/\s+(and|&)\s+/gi, ', ')
    .split(',')
    .map(token => token.trim().replace(/^["'([{]+|["')\]}]+$/g, '').replace(/[.;:]$/g, '').trim())
    .filter(Boolean);
}

export function noteRewriteExtractHashesFromContinuesAs(value = '') {
  const raw = normalizeCell(value);
  if (!raw || /^path ends\.?$/i.test(raw)) return [];
  const lower = raw.toLowerCase();
  if (!lower.startsWith('continues as')) return [];
  return raw
    .replace(/^continues as\s*/i, '')
    .split(',')
    .map(token => token.trim().replace(/[.;]$/g, '').trim())
    .filter(Boolean);
}

export function noteRewriteFormatStructuredList(labels = []) {
  const unique = [];
  const seen = new Set();
  (Array.isArray(labels) ? labels : []).forEach((label) => {
    const clean = normalizeCell(label);
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(clean);
  });
  unique.sort(noteRewriteCompareLabels);
  return unique.join(', ');
}

export function noteRewriteSectionRegex() {
  return new RegExp(CASE_NOTE_HASH_LABEL_REWRITE_SECTION_REGEX_SOURCE, 'gi');
}

export function noteRewriteBuildDesiredFromQ(row = [], maps = {}, rowIndex = 0) {
  const qRaw = normalizeCell(row?.[16]);
  const contextLabel = normalizeCell(row?.[0]);
  const hashes = noteRewriteExtractHashesFromContinuesAs(qRaw);
  const resolved = hashes.map(hash => noteRewriteResolveHashToLabel(hash, maps, contextLabel, rowIndex));
  const missing = resolved.filter(item => !item.label).map(item => item.hash);
  const labels = resolved.map(item => item.label).filter(Boolean);
  const labelList = noteRewriteFormatStructuredList(labels);
  const prefix = labels.length > 1 ? 'Branches as' : 'Continues as';
  return {
    qRaw,
    hashes,
    resolved,
    missing,
    labels,
    labelList,
    prefix,
    desiredSection: labelList ? `${prefix} ${labelList}` : '',
    ambiguousCount: resolved.filter(item => item.ambiguous).length
  };
}

export function noteRewriteReplaceHashTokensInText(text = '', maps = {}, contextLabel = '', rowIndex = 0) {
  const source = String(text || '');
  if (!source) return { text: source, replacements: [], unresolved: [], ambiguousCount: 0 };
  const patterns = Array.from(maps?.exact?.keys?.() || []).filter(Boolean).sort((left, right) => right.length - left.length);
  if (patterns.length === 0) return { text: source, replacements: [], unresolved: [], ambiguousCount: 0 };
  const replacements = [];
  let ambiguousCount = 0;

  patterns.forEach((pattern) => {
    let start = source.indexOf(pattern);
    while (start !== -1) {
      const end = start + pattern.length - 1;
      if (noteRewriteBoundaryOk(source, start, end, pattern)) {
        const resolved = noteRewriteResolveHashToLabel(pattern, maps, contextLabel, rowIndex);
        if (resolved.label) {
          replacements.push({ start, endExclusive: end + 1, hash: pattern, replacement: resolved.label, ambiguous: resolved.ambiguous });
          if (resolved.ambiguous) ambiguousCount += 1;
        }
      }
      start = source.indexOf(pattern, start + 1);
    }
  });

  replacements.sort((left, right) => {
    if (left.start !== right.start) return left.start - right.start;
    return (right.endExclusive - right.start) - (left.endExclusive - left.start);
  });

  const picked = [];
  let lastEnd = -1;
  replacements.forEach((replacement) => {
    if (replacement.start >= lastEnd) {
      picked.push(replacement);
      lastEnd = replacement.endExclusive;
    }
  });

  if (picked.length === 0) return { text: source, replacements: [], unresolved: [], ambiguousCount: 0 };
  let output = '';
  let cursor = 0;
  picked.forEach((replacement) => {
    output += source.slice(cursor, replacement.start);
    output += replacement.replacement;
    cursor = replacement.endExclusive;
  });
  output += source.slice(cursor);
  return { text: output, replacements: picked, unresolved: [], ambiguousCount };
}

export function noteRewriteNormalizeStructuredSections(notes = '', row = [], maps = {}, rowIndex = 0, options = {}) {
  const original = String(notes || '');
  const qDesired = noteRewriteBuildDesiredFromQ(row, maps, rowIndex);
  const regex = noteRewriteSectionRegex();
  let match;
  let output = '';
  let cursor = 0;
  let sectionCount = 0;
  let replacementCount = 0;
  let ambiguousCount = 0;
  const unresolved = [];
  const sectionSummaries = [];

  while ((match = regex.exec(original)) !== null) {
    const full = match[0];
    const prefixRaw = match[1] || '';
    const listRaw = match[2] || '';
    const terminator = match[3] || '';
    const start = match.index;
    const end = start + full.length;
    const listTokens = noteRewriteSplitStructuredList(listRaw);
    let nextSection = full;
    let reason = '';
    let tokenReplacements = [];

    if (qDesired.desiredSection && qDesired.missing.length === 0) {
      const desiredPrefix = qDesired.labels.length > 1 ? 'Branches as' : 'Continues as';
      const desired = `${desiredPrefix} ${qDesired.labelList}`;
      const ender = terminator || '.';
      nextSection = `${desired}${ender}`;
      reason = 'Refreshed from current Column Q hashes and current Column A path labels.';
      ambiguousCount += qDesired.ambiguousCount;
      tokenReplacements = qDesired.resolved.filter(item => item.label).map(item => ({ hash: item.hash, label: item.label, ambiguous: item.ambiguous }));
    } else {
      const labels = [];
      const missingTokens = [];
      listTokens.forEach((token) => {
        const resolved = noteRewriteResolveHashToLabel(token, maps, normalizeCell(row?.[0]), rowIndex);
        if (resolved.label) {
          labels.push(resolved.label);
          tokenReplacements.push({ hash: token, label: resolved.label, ambiguous: resolved.ambiguous });
          if (resolved.ambiguous) ambiguousCount += 1;
        } else if (noteRewriteIsLikelyPathLabel(token)) {
          labels.push(token);
        } else {
          missingTokens.push(token);
        }
      });

      if (missingTokens.length === 0 && labels.length > 0) {
        const desiredPrefix = labels.length > 1 || /^branches as$/i.test(prefixRaw) ? 'Branches as' : 'Continues as';
        const sortedList = noteRewriteFormatStructuredList(labels);
        nextSection = `${desiredPrefix} ${sortedList}${terminator || '.'}`;
        reason = 'Normalized the existing structured note list and replaced any hash tokens found inside it.';
      } else {
        missingTokens.forEach(token => unresolved.push(token));
      }
    }

    output += original.slice(cursor, start);
    output += nextSection;
    cursor = end;

    if (normalizeCell(nextSection) !== normalizeCell(full)) {
      sectionCount += 1;
      replacementCount += Math.max(1, tokenReplacements.length);
      sectionSummaries.push({ before: full, after: nextSection, reason, tokenReplacements });
    }
  }

  output += original.slice(cursor);

  if (options.includeUnstructured && qDesired.hashes.length === 0) {
    const unstructured = noteRewriteReplaceHashTokensInText(output, maps, normalizeCell(row?.[0]), rowIndex);
    if (normalizeCell(unstructured.text) !== normalizeCell(output)) {
      replacementCount += unstructured.replacements.length;
      ambiguousCount += unstructured.ambiguousCount;
      sectionSummaries.push({ before: output, after: unstructured.text, reason: 'Optional broad token replacement outside structured Continues/Branches sections.', tokenReplacements: unstructured.replacements.map(item => ({ hash: item.hash, label: item.replacement, ambiguous: item.ambiguous })) });
      output = unstructured.text;
    }
  }

  return {
    notes: output.replace(/\s+([.,;:])/g, '$1').replace(/\s{2,}/g, ' ').trim(),
    sectionCount,
    replacementCount,
    ambiguousCount,
    unresolved: Array.from(new Set(unresolved.concat(qDesired.missing))).filter(Boolean),
    qDesired,
    sectionSummaries
  };
}

export function buildCaseNoteHashRewriteReviewKey(caseId = '', rowIndex = 0, currentNotes = '', proposedNotes = '', qRaw = '') {
  return [
    CASE_NOTE_HASH_LABEL_REWRITE_FAMILY,
    normalizeCell(caseId) || 'case',
    rowIndex,
    caseBuddyHashString(currentNotes || ''),
    caseBuddyHashString(proposedNotes || ''),
    caseBuddyHashString(qRaw || '')
  ].join('|');
}

export function buildCaseNoteHashRewriteBackupRows(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  return data
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row, rowIndex }) => rowIndex > 0 && rowHasMeaningfulData(row))
    .map(({ row, rowIndex }) => ({
      rowIndex,
      rowNumber: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      transactionHash: normalizeCell(row?.[1]),
      fromAddress: normalizeCell(row?.[2]),
      toAddress: normalizeCell(row?.[3]),
      fromHash: normalizeCell(row?.[14]),
      continuesAs: normalizeCell(row?.[16]),
      originalNotes: normalizeCell(row?.[9]),
      notesHash: caseBuddyHashString(normalizeCell(row?.[9])),
      rowIdentity: `${rowIndex}|${normalizeCell(row?.[0])}|${normalizeCell(row?.[1])}|${caseBuddyHashString(normalizeCell(row?.[9]))}`
    }));
}

export function buildCaseNoteHashRewriteBackupRecord(mainData = [], options = {}) {
  const rows = buildCaseNoteHashRewriteBackupRows(mainData);
  return {
    createdAtUtc: new Date().toISOString(),
    revision: Number(options.revision) || 0,
    caseId: normalizeCell(options.caseId) || 'case',
    rowCount: rows.length,
    nonBlankNotesCount: rows.filter(row => normalizeCell(row.originalNotes)).length,
    rows,
    basisHash: `notes-backup-${caseBuddyHashString(JSON.stringify(rows.map(row => [row.rowIndex, row.pathLabel, row.transactionHash, row.notesHash])))}`
  };
}

export function mergeCaseNoteHashRewriteBackupRecords(existing = null, next = null) {
  if (!existing?.rows?.length) return next;
  if (!next?.rows?.length) return existing;
  const rows = Array.isArray(existing.rows) ? existing.rows.slice() : [];
  const seen = new Set(rows.map(row => row.rowIdentity || `${row.rowIndex}|${row.pathLabel}|${row.transactionHash}|${row.notesHash}`));
  next.rows.forEach((row) => {
    const key = row.rowIdentity || `${row.rowIndex}|${row.pathLabel}|${row.transactionHash}|${row.notesHash}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  });
  rows.sort((left, right) => (left.rowNumber || 0) - (right.rowNumber || 0));
  return {
    ...existing,
    updatedAtUtc: new Date().toISOString(),
    revision: Math.max(Number(existing.revision) || 0, Number(next.revision) || 0),
    rowCount: rows.length,
    nonBlankNotesCount: rows.filter(row => normalizeCell(row.originalNotes)).length,
    rows,
    basisHash: `notes-backup-${caseBuddyHashString(JSON.stringify(rows.map(row => [row.rowIndex, row.pathLabel, row.transactionHash, row.notesHash])))}`
  };
}

export function buildCaseNoteHashRewriteBackupOutputRows(backupRecord = null) {
  const backup = backupRecord && typeof backupRecord === 'object' ? backupRecord : {};
  const rows = [
    ['Original Notes Backup', '', '', '', '', '', '', '', '', 'Created before hash-to-path-label note rewrites. Use this as audit/support data; Main Data remains the case source of truth.'],
    ['Created At UTC', backup.createdAtUtc || '', 'Updated At UTC', backup.updatedAtUtc || '', 'Revision', backup.revision ?? '', 'Rows', backup.rowCount || 0, 'Nonblank Notes', backup.nonBlankNotesCount || 0],
    ['Row', 'Path Label', 'Transaction Hash', 'From Address', 'To Address', 'From Hash', 'Column Q', 'Original Column J Notes', 'Notes Hash', 'Row Identity']
  ];
  (Array.isArray(backup.rows) ? backup.rows : []).forEach((row) => {
    rows.push([
      row.rowNumber || '',
      row.pathLabel || '',
      row.transactionHash || '',
      row.fromAddress || '',
      row.toAddress || '',
      row.fromHash || '',
      row.continuesAs || '',
      row.originalNotes || '',
      row.notesHash || '',
      row.rowIdentity || ''
    ]);
  });
  return rows;
}

export function buildCaseNoteHashRewriteAnalysis(mainData = [], workflowState = {}, options = {}) {
  const data = normalizeCaseMatrix(mainData);
  const maps = buildNoteRewriteHashCandidateMaps(data);
  const caseId = normalizeCell(options.caseId) || 'case';
  const includeUnstructured = !!options.includeUnstructured;
  const reviewState = workflowState?.noteHashPathLabelRewriteReview && typeof workflowState.noteHashPathLabelRewriteReview === 'object'
    ? workflowState.noteHashPathLabelRewriteReview
    : {};
  const backup = reviewState.backup && typeof reviewState.backup === 'object' ? reviewState.backup : null;
  const candidates = [];
  let rowsScanned = 0;
  let structuredSectionsScanned = 0;
  let unresolvedHashCount = 0;
  let ambiguousReplacementCount = 0;

  data.forEach((row, rowIndex) => {
    if (rowIndex === 0 || !rowHasMeaningfulData(row)) return;
    rowsScanned += 1;
    const currentNotes = normalizeCell(row?.[9]);
    if (!currentNotes) return;
    const qRaw = normalizeCell(row?.[16]);
    const proposal = noteRewriteNormalizeStructuredSections(currentNotes, row, maps, rowIndex, { includeUnstructured });
    structuredSectionsScanned += proposal.sectionSummaries.length;
    unresolvedHashCount += proposal.unresolved.length;
    ambiguousReplacementCount += proposal.ambiguousCount;
    const proposedNotes = normalizeCell(proposal.notes);
    if (proposedNotes === currentNotes && proposal.unresolved.length === 0) return;
    const reviewKey = buildCaseNoteHashRewriteReviewKey(caseId, rowIndex, currentNotes, proposedNotes, qRaw);
    const itemKind = proposal.unresolved.length > 0 ? 'unresolved' : 'rewrite';
    const item = {
      itemKind,
      itemKindLabel: itemKind === 'unresolved' ? 'Needs path-label map' : 'Hash-to-label rewrite',
      reviewKey,
      rowIndex,
      rowNumber: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      transactionHash: normalizeCell(row?.[1]),
      currentNotes,
      proposedNotes,
      continuesAs: qRaw,
      fromHash: normalizeCell(row?.[14]),
      attribution: normalizeCell(row?.[10]),
      analystNotes: normalizeCell(row?.[11]),
      replacementCount: proposal.replacementCount,
      sectionCount: proposal.sectionCount,
      ambiguousCount: proposal.ambiguousCount,
      unresolvedHashes: proposal.unresolved,
      qHashes: proposal.qDesired.hashes,
      qLabels: proposal.qDesired.labels,
      sectionSummaries: proposal.sectionSummaries,
      isEligible: proposal.unresolved.length === 0 && proposedNotes !== currentNotes,
      statusLabel: proposal.unresolved.length > 0 ? 'Needs labels first' : proposedNotes !== currentNotes ? 'Ready to rewrite' : 'Current',
      reason: proposal.unresolved.length > 0
        ? `Could not resolve ${proposal.unresolved.length} referenced hash${proposal.unresolved.length === 1 ? '' : 'es'} to current path labels.`
        : proposal.qDesired.hashes.length > 0
          ? 'Refreshes Continues/Branches note wording from current Column Q hashes and current Column A path labels.'
          : 'Replaces exact transaction-hash tokens inside structured note wording.'
    };
    item.searchText = [
      item.rowNumber,
      item.pathLabel,
      item.transactionHash,
      item.continuesAs,
      item.currentNotes,
      item.proposedNotes,
      item.reason,
      item.attribution,
      item.analystNotes,
      item.qHashes.join(' '),
      item.qLabels.join(' '),
      item.unresolvedHashes.join(' ')
    ].map(value => normalizeCell(value).toLowerCase()).filter(Boolean).join(' ');
    candidates.push(item);
  });

  candidates.sort((left, right) => {
    if (left.isEligible !== right.isEligible) return left.isEligible ? -1 : 1;
    return (left.rowNumber || 0) - (right.rowNumber || 0);
  });
  const eligibleItems = candidates.filter(item => item.isEligible);
  const unresolvedItems = candidates.filter(item => item.itemKind === 'unresolved');
  const basisPayload = candidates.map(item => ({ key: item.reviewKey, row: item.rowIndex, proposed: caseBuddyHashString(item.proposedNotes), unresolved: item.unresolvedHashes })).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  const currentBasisHash = `note-hash-label-rewrite-${caseBuddyHashString(JSON.stringify(basisPayload))}`;
  const completedBasisHash = normalizeCell(reviewState.completedBasisHash);
  const backupExists = !!(backup && Array.isArray(backup.rows) && backup.rows.length > 0);
  const backupRowCount = backupExists ? backup.rows.length : 0;
  const backupRevision = Number.isFinite(Number(backup?.revision)) ? Number(backup.revision) : null;
  const isCurrent = !!completedBasisHash && completedBasisHash === currentBasisHash && eligibleItems.length === 0 && backupExists;
  let summary = '';
  if (!backupExists && eligibleItems.length > 0) {
    summary = `${eligibleItems.length} note rewrite${eligibleItems.length === 1 ? '' : 's'} are ready, but create/merge an original notes backup before applying them.`;
  } else if (eligibleItems.length > 0) {
    summary = `${eligibleItems.length} hash-to-path-label note rewrite${eligibleItems.length === 1 ? '' : 's'} are ready. Re-run this step if path labels change later.`;
  } else if (unresolvedItems.length > 0) {
    summary = `${unresolvedItems.length} note row${unresolvedItems.length === 1 ? '' : 's'} reference hashes that do not currently resolve to path labels.`;
  } else if (backupExists) {
    summary = 'Original notes backup exists and no current hash-to-path-label note rewrites remain.';
  } else {
    summary = 'No current hash-to-path-label rewrites were found. Create a notes backup or manually complete if this case does not need the rewrite.';
  }

  return {
    rowsScanned,
    structuredSectionsScanned,
    totalCandidateCount: candidates.length,
    eligibleCount: eligibleItems.length,
    unresolvedCount: unresolvedItems.length,
    replacementCount: eligibleItems.reduce((sum, item) => sum + (item.replacementCount || 0), 0),
    ambiguousReplacementCount,
    unresolvedHashCount,
    candidates,
    items: candidates,
    eligibleItems,
    unresolvedItems,
    backup,
    backupExists,
    backupRowCount,
    backupRevision,
    currentBasisHash,
    completedBasisHash,
    isCurrent,
    pass: backupExists && eligibleItems.length === 0,
    summary
  };
}

export function applyCaseNoteHashRewriteItemsToMainMatrix(mainData = [], selectedItems = []) {
  const data = normalizeCaseMatrix(mainData);
  const items = (Array.isArray(selectedItems) ? selectedItems : []).filter(item => item?.reviewKey && item.isEligible);
  let rowsUpdated = 0;
  let replacementsApplied = 0;
  let skippedCount = 0;
  const updatedRows = [];
  const seenRows = new Set();

  items.forEach((item) => {
    const rowIndex = Number(item.rowIndex);
    if (!Number.isInteger(rowIndex) || rowIndex <= 0 || rowIndex >= data.length || seenRows.has(rowIndex)) {
      skippedCount += 1;
      return;
    }
    seenRows.add(rowIndex);
    const row = data[rowIndex];
    if (!Array.isArray(row)) {
      skippedCount += 1;
      return;
    }
    const currentNotes = normalizeCell(row?.[9]);
    const currentQ = normalizeCell(row?.[16]);
    if (currentNotes !== normalizeCell(item.currentNotes) || currentQ !== normalizeCell(item.continuesAs)) {
      skippedCount += 1;
      return;
    }
    const proposedNotes = normalizeCell(item.proposedNotes);
    if (!proposedNotes || proposedNotes === currentNotes) {
      skippedCount += 1;
      return;
    }
    row[9] = proposedNotes;
    rowsUpdated += 1;
    replacementsApplied += Math.max(1, Number(item.replacementCount) || 1);
    updatedRows.push({
      rowIndex,
      rowNumber: rowIndex + 1,
      pathLabel: item.pathLabel,
      transactionHash: item.transactionHash,
      continuesAs: item.continuesAs,
      currentNotes,
      proposedNotes
    });
  });

  return { data, changed: rowsUpdated > 0, rowsUpdated, replacementsApplied, skippedCount, updatedRows };
}

export function restoreCaseNoteHashRewriteBackupToMainMatrix(mainData = [], backupRecord = null) {
  const data = normalizeCaseMatrix(mainData);
  const rows = Array.isArray(backupRecord?.rows) ? backupRecord.rows : [];
  let rowsRestored = 0;
  let skippedCount = 0;
  const restoredRows = [];

  rows.forEach((backupRow) => {
    const rowIndex = Number(backupRow.rowIndex);
    if (!Number.isInteger(rowIndex) || rowIndex <= 0 || rowIndex >= data.length) {
      skippedCount += 1;
      return;
    }
    const row = data[rowIndex];
    if (!Array.isArray(row)) {
      skippedCount += 1;
      return;
    }
    const currentHash = normalizeCell(row?.[1]);
    const currentLabel = normalizeCell(row?.[0]);
    const backupHash = normalizeCell(backupRow.transactionHash);
    const backupLabel = normalizeCell(backupRow.pathLabel);
    if (backupHash && currentHash && currentHash !== backupHash) {
      skippedCount += 1;
      return;
    }
    if (backupLabel && currentLabel && currentLabel !== backupLabel) {
      skippedCount += 1;
      return;
    }
    const originalNotes = normalizeCell(backupRow.originalNotes);
    if (normalizeCell(row?.[9]) === originalNotes) return;
    row[9] = originalNotes;
    rowsRestored += 1;
    restoredRows.push({ rowIndex, rowNumber: rowIndex + 1, pathLabel: currentLabel || backupLabel, transactionHash: currentHash || backupHash, originalNotes });
  });

  return { data, changed: rowsRestored > 0, rowsRestored, skippedCount, restoredRows };
}

export function buildCaseNoteHashRewriteAuditRows(analysis = {}, actionLabel = '') {
  const rows = [
    ['Case Buddy Note Hash To Path Label Rewrite Audit', '', '', '', '', '', '', '', '', 'Audit only. Main Data Column J remains the source of truth.'],
    ['Action', actionLabel || 'Review refreshed', 'Eligible Rewrites', analysis.eligibleCount || 0, 'Unresolved', analysis.unresolvedCount || 0, 'Backup Rows', analysis.backupRowCount || 0, 'Basis', analysis.currentBasisHash || ''],
    ['Status', analysis.summary || '', 'Backup Created', analysis.backup?.createdAtUtc || '', 'Backup Revision', analysis.backupRevision ?? '', '', '', '', ''],
    ['Kind', 'Status', 'Row', 'Path Label', 'Hash', 'Column Q', 'Current Notes', 'Proposed Notes', 'Reason', 'Unresolved Hashes']
  ];
  (analysis.candidates || []).forEach((item) => {
    rows.push([
      item.itemKindLabel || item.itemKind || '',
      item.statusLabel || '',
      item.rowNumber || '',
      item.pathLabel || '',
      item.transactionHash || '',
      item.continuesAs || '',
      item.currentNotes || '',
      item.proposedNotes || '',
      item.reason || '',
      (item.unresolvedHashes || []).join(', ')
    ]);
  });
  rows.push([]);
  rows.push(['Original Notes Backup Snapshot', '', '', '', '', '', '', '', '', '']);
  buildCaseNoteHashRewriteBackupOutputRows(analysis.backup).forEach(row => rows.push(row));
  return rows;
}
